// src/components/customers/CustomerDetail.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api';
import { updateCustomerAPI, fetchAllTags, fetchCustomerTags, updateCustomerTags } from '../../api/customers.api';
import { fetchPrestations } from '../../api/prestations.api';
import { fetchProjects } from '../../api/projects.api';
import {
  ArrowLeft, Pencil, X, Loader2, User, Phone, Mail, MapPin,
  Star, CreditCard, BellOff, MessageSquareOff, FileText,
  ShoppingCart, Briefcase, FolderKanban, AlertTriangle, CheckCircle2,
  Clock, DollarSign, ToggleLeft, ToggleRight, Building2, Tag, Plus,
  Download, ChevronDown, ChevronUp, Bell, CalendarDays, MessageSquare, Send, History,
} from 'lucide-react';

/* ── utils ── */
function cx(...classes) { return classes.filter(Boolean).join(' '); }
function toNum(v, def = 0) { const n = Number(v); return Number.isFinite(n) ? n : def; }
function isValidDate(d) { return d instanceof Date && !Number.isNaN(d.getTime()); }
function fmtMoney(v) { return toNum(v).toLocaleString('fr-FR') + ' FCFA'; }
function fmtDate(dLike) {
  const d = new Date(dLike);
  return isValidDate(d) ? d.toLocaleDateString('fr-FR') : '—';
}
function daysBetween(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  if (!isValidDate(d)) return NaN;
  d.setHours(0, 0, 0, 0); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

/* ── petits composants ── */
function Pill({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
    green:   'bg-emerald-50 text-emerald-700 ring-emerald-100',
    yellow:  'bg-amber-50 text-amber-700 ring-amber-100',
    blue:    'bg-sky-50 text-sky-700 ring-sky-100',
    red:     'bg-rose-50 text-rose-700 ring-rose-100',
    purple:  'bg-violet-50 text-violet-700 ring-violet-100',
    gold:    'bg-yellow-50 text-yellow-700 ring-yellow-200',
    silver:  'bg-slate-100 text-slate-600 ring-slate-200',
    bronze:  'bg-orange-50 text-orange-700 ring-orange-200',
  };
  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1', tones[tone] || tones.neutral)}>
      {children}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = 'neutral' }) {
  const colors = {
    neutral: 'text-slate-700 bg-slate-50 border-slate-200',
    green:   'text-emerald-700 bg-emerald-50 border-emerald-100',
    red:     'text-rose-700 bg-rose-50 border-rose-100',
    blue:    'text-sky-700 bg-sky-50 border-sky-100',
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
          {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
        </div>
        <div className={cx('rounded-xl border p-2', colors[tone])}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

/* ── Bannière rappels ── */
function RemindersBanner({ installments }) {
  const info = useMemo(() => {
    const src = Array.isArray(installments) ? installments : [];
    const pending = src.filter(i => {
      const paid = toNum(i?.amount_paid);
      return i?.status !== 'paid' && paid < toNum(i?.amount);
    });
    const sorted = [...pending].sort((a, b) => new Date(a?.due_date) - new Date(b?.due_date));
    const overdue = [], soon = [];
    for (const i of sorted) {
      const d = daysBetween(i?.due_date);
      if (!Number.isFinite(d)) continue;
      if (d < 0) overdue.push(i); else if (d <= 3) soon.push(i);
    }
    return { overdue, soon, next3: sorted.slice(0, 3) };
  }, [installments]);

  if (!Array.isArray(installments) || (info.overdue.length === 0 && info.soon.length === 0)) return null;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-wrap gap-3">
        {info.overdue.length > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700">
            <AlertTriangle className="h-4 w-4" /> {info.overdue.length} en retard
          </span>
        )}
        {info.soon.length > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-800">
            <Clock className="h-4 w-4" /> {info.soon.length} échéance(s) ≤ 3 jours
          </span>
        )}
      </div>
      {info.next3.length > 0 && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {info.next3.map((i, idx) => {
            const d = daysBetween(i?.due_date);
            const isOv = Number.isFinite(d) && d < 0;
            const isSoon = Number.isFinite(d) && d >= 0 && d <= 3;
            return (
              <div key={i?.installment_id ?? idx} className={cx('rounded-xl border p-3', isOv ? 'border-rose-200 bg-white' : 'border-amber-200 bg-white')}>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{fmtDate(i?.due_date)}</span>
                  {isOv && <span className="font-semibold text-rose-600">En retard</span>}
                  {isSoon && !isOv && <span className="font-semibold text-amber-700">Dans {d}j</span>}
                </div>
                <div className="mt-1 text-sm font-bold text-slate-900">{fmtMoney(Math.max(toNum(i?.amount) - toNum(i?.amount_paid), 0) || toNum(i?.amount))}</div>
                {i?.order_external_id && <div className="text-xs text-slate-400 mt-0.5">{i.order_external_id}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Balance block ── */
function BalanceBlock({ balance }) {
  if (!balance) return null;
  const { total_due, overdue_total, orders_due, prestations_due, items } = balance;

  if (total_due <= 0) return (
    <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
      <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
      <div>
        <div className="text-sm font-semibold text-emerald-800">Aucune dette en cours</div>
        <div className="text-xs text-emerald-700 mt-0.5">Ce client est à jour de tous ses règlements.</div>
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border border-rose-200 bg-white overflow-hidden shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-rose-50 px-5 py-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-rose-500">Solde dû total</div>
          <div className="mt-1 text-2xl font-black text-rose-700">{fmtMoney(total_due)}</div>
          {overdue_total > 0 && <div className="mt-1 text-xs font-medium text-rose-600">dont {fmtMoney(overdue_total)} en retard</div>}
        </div>
        <div className="flex gap-5 text-sm">
          {orders_due > 0 && <div className="text-center"><div className="text-xs text-slate-500">Commandes crédit</div><div className="font-bold text-slate-900">{fmtMoney(orders_due)}</div></div>}
          {prestations_due > 0 && <div className="text-center"><div className="text-xs text-slate-500">Prestations</div><div className="font-bold text-slate-900">{fmtMoney(prestations_due)}</div></div>}
        </div>
      </div>
      {items?.length > 0 && (
        <div className="divide-y divide-slate-100">
          {items.map((item, idx) => (
            <div key={idx} className={cx('flex items-center justify-between px-5 py-3', item.overdue && 'bg-rose-50/40')}>
              <div className="flex items-center gap-3 min-w-0">
                <span className={cx('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  item.type === 'order_credit' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700'
                )}>
                  {item.type === 'order_credit' ? 'Commande' : 'Prestation'}
                </span>
                <span className="truncate text-sm font-medium text-slate-700">{item.reference || '—'}</span>
              </div>
              <div className="flex items-center gap-4 ml-3 shrink-0">
                {item.due_date && (
                  <span className={cx('text-xs font-medium', item.overdue ? 'text-rose-600' : 'text-slate-500')}>
                    {item.overdue ? '⚠ ' : ''}Éch. {new Date(item.due_date).toLocaleDateString('fr-FR')}
                  </span>
                )}
                <span className="text-sm font-bold text-slate-900">{fmtMoney(item.amount_due)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Modal Shell ── */
function ModalShell({ title, onClose, children }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl my-8 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="text-lg font-bold text-slate-900">{title}</div>
          <button onClick={onClose} className="rounded-xl border border-slate-200 bg-white p-2 hover:bg-slate-50">
            <X className="h-4 w-4 text-slate-700" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

/* ── Configs ── */
const LOYALTY_CONFIG = {
  bronze: { label: 'Bronze', tone: 'bronze', icon: '🥉' },
  silver: { label: 'Silver', tone: 'silver', icon: '🥈' },
  gold:   { label: 'Gold',   tone: 'gold',   icon: '🥇' },
};
const CREDIT_STATUS_CONFIG = {
  active:    { label: 'Crédit OK',    tone: 'green', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
  suspended: { label: 'Suspendu',     tone: 'yellow', cls: 'bg-amber-50 text-amber-700 ring-amber-100' },
  blocked:   { label: 'Bloqué',       tone: 'red', cls: 'bg-rose-50 text-rose-700 ring-rose-100' },
};
const PRESTATION_STATUS = {
  DRAFT: { label: 'Brouillon', tone: 'neutral' },
  PENDING: { label: 'En attente', tone: 'yellow' },
  IN_PROGRESS: { label: 'En cours', tone: 'blue' },
  PARTIALLY_PAID: { label: 'Partiel', tone: 'purple' },
  PAID: { label: 'Payé', tone: 'green' },
  CANCELLED: { label: 'Annulé', tone: 'red' },
};
const PROJECT_STATUS = {
  DRAFT:     { label: 'Brouillon', tone: 'neutral', pct: 20 },
  ACTIVE:    { label: 'Actif',     tone: 'green',   pct: 60 },
  ON_HOLD:   { label: 'En pause',  tone: 'yellow',  pct: 45 },
  DONE:      { label: 'Terminé',   tone: 'blue',    pct: 100 },
  CANCELLED: { label: 'Annulé',    tone: 'red',     pct: 0 },
};

function tagTone(name) {
  const n = (name || '').toLowerCase();
  if (n === 'vip') return 'gold';
  if (n.includes('fidèle') || n.includes('fidele')) return 'green';
  if (n.includes('relancer')) return 'red';
  return 'blue';
}

/* ── Timeline unifiée ── */
const COMM_STATUS = {
  SENT:    { label: 'Envoyé',  cls: 'bg-emerald-50 text-emerald-700' },
  FAILED:  { label: 'Échec',   cls: 'bg-rose-50 text-rose-700' },
  PENDING: { label: 'En file', cls: 'bg-amber-50 text-amber-700' },
};
const PAYMENT_MODE_LABELS = {
  cash: 'Espèces', credit: 'Crédit', echelonne: 'Échelonné', deferred: 'Différé',
};

function UnifiedTimeline({ orders, prestations, communications, installments }) {
  const items = useMemo(() => {
    const list = [];

    for (const o of (orders || [])) {
      list.push({
        type: 'order',
        date: o.created_at || o.sale_date,
        id: o.id,
        title: o.external_id || `Commande`,
        sub: PAYMENT_MODE_LABELS[o.payment_mode] || o.payment_mode || '',
        amount: o.total_ttc,
        status: o.payment_status || o.payment_mode,
        statusCls: o.payment_mode === 'cash' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700',
        statusLabel: o.payment_mode === 'cash' ? 'Comptant' : (o.payment_mode === 'credit' ? 'Crédit' : 'Échelonné'),
        icon: ShoppingCart,
        iconCls: 'bg-sky-100 text-sky-600',
      });
    }

    for (const p of (prestations || [])) {
      const st = PRESTATION_STATUS[p.status] || PRESTATION_STATUS.DRAFT;
      list.push({
        type: 'prestation',
        date: p.created_at || p.service_date,
        id: p.id,
        title: p.title || p.code,
        sub: p.code || '',
        amount: p.amount_ttc || p.amount,
        due: toNum(p.due_total),
        statusLabel: st.label,
        statusCls: {
          neutral: 'bg-slate-100 text-slate-600',
          yellow:  'bg-amber-50 text-amber-700',
          blue:    'bg-sky-50 text-sky-700',
          purple:  'bg-violet-50 text-violet-700',
          green:   'bg-emerald-50 text-emerald-700',
          red:     'bg-rose-50 text-rose-700',
        }[st.tone] || 'bg-slate-100 text-slate-600',
        icon: Briefcase,
        iconCls: 'bg-violet-100 text-violet-600',
      });
    }

    for (const c of (communications || [])) {
      const st = COMM_STATUS[c.status] || COMM_STATUS.PENDING;
      const isWA = c.channel === 'WHATSAPP';
      list.push({
        type: 'comm',
        date: c.sent_at || c.created_at,
        id: c.id,
        title: c.subject || (isWA ? 'Message WhatsApp' : 'Email envoyé'),
        sub: c.to_address || '',
        statusLabel: st.label,
        statusCls: st.cls,
        icon: isWA ? MessageSquare : Send,
        iconCls: isWA ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600',
      });
    }

    const overdueInst = (installments || []).filter(i => {
      const d = daysBetween(i?.due_date);
      return Number.isFinite(d) && d < 0 && i?.status !== 'paid';
    });
    for (const i of overdueInst) {
      list.push({
        type: 'installment',
        date: i.due_date,
        id: i.installment_id,
        title: `Échéance en retard`,
        sub: i.order_external_id || '',
        amount: Math.max(toNum(i.amount) - toNum(i.amount_paid), 0),
        statusLabel: 'En retard',
        statusCls: 'bg-rose-50 text-rose-700',
        icon: AlertTriangle,
        iconCls: 'bg-rose-100 text-rose-600',
      });
    }

    list.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    return list;
  }, [orders, prestations, communications, installments]);

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
        Aucun événement trouvé.
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Ligne verticale */}
      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-100" />
      <div className="space-y-1">
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={`${item.type}-${item.id ?? idx}`} className="relative flex items-start gap-4 rounded-2xl bg-white px-4 py-3.5 hover:bg-slate-50 transition-colors">
              {/* Icône sur la ligne */}
              <div className={cx('relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', item.iconCls)}>
                <Icon className="h-4 w-4" />
              </div>
              {/* Contenu */}
              <div className="flex flex-1 items-start justify-between gap-3 min-w-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900 truncate">{item.title}</span>
                    <span className={cx('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', item.statusCls)}>
                      {item.statusLabel}
                    </span>
                  </div>
                  {item.sub && <div className="mt-0.5 text-xs text-slate-500 truncate">{item.sub}</div>}
                </div>
                <div className="text-right shrink-0">
                  {item.amount != null && (
                    <div className="text-sm font-bold text-slate-900">{fmtMoney(item.amount)}</div>
                  )}
                  {item.due > 0 && (
                    <div className="text-xs font-medium text-rose-600">Reste: {fmtMoney(item.due)}</div>
                  )}
                  <div className="mt-0.5 text-xs text-slate-400">{fmtDate(item.date)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   Page principale
═══════════════════════════════════════ */
export default function CustomerDetail() {
  const { uid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]         = useState(true);
  const [customer, setCustomer]       = useState(null);
  const [orders, setOrders]           = useState([]);
  const [installments, setInstallments] = useState([]);
  const [balance, setBalance]         = useState(null);
  const [prestations, setPrestations] = useState([]);
  const [projects, setProjects]       = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [customerTags, setCustomerTags]   = useState([]);
  const [rfm, setRfm]                 = useState(null);
  const [communications, setCommunications] = useState([]);
  const [commLoading, setCommLoading]       = useState(false);
  const [commLoaded, setCommLoaded]         = useState(false);
  const [error, setError]             = useState('');
  const [activeTab, setActiveTab]     = useState('apercu');

  // Édition
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm]           = useState({});
  const [saving, setSaving]               = useState(false);
  const [newTagInput, setNewTagInput]     = useState('');
  const [newTagLoading, setNewTagLoading] = useState(false);

  // Relevé de compte
  const todayStr = new Date().toISOString().slice(0, 10);
  const jan1Str  = `${new Date().getFullYear()}-01-01`;
  const [showStatement, setShowStatement]       = useState(false);
  const [statementFrom, setStatementFrom]       = useState(jan1Str);
  const [statementTo, setStatementTo]           = useState(todayStr);
  const [statementLoading, setStatementLoading] = useState(false);

  async function downloadStatement() {
    if (statementLoading) return;
    setStatementLoading(true);
    try {
      const params = new URLSearchParams({ from: statementFrom, to: statementTo, format: 'pdf' });
      const res = await fetch(`/api/customers/${uid}/statement?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `releve-${uid}-${statementFrom}-${statementTo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.message || 'Erreur lors du téléchargement du relevé');
    } finally {
      setStatementLoading(false);
    }
  }

  async function load() {
    try {
      setLoading(true);
      setError('');
      const c = await apiFetch(`/api/customers/${uid}`);
      setCustomer(c && typeof c === 'object' ? c : null);

      if (c?.id) {
        const [o, p, b, pr, pj, allTags, cTags, rfmData] = await Promise.all([
          apiFetch(`/api/customers/${uid}/orders`).catch(() => ({})),
          apiFetch(`/api/payments/customers/${uid}/installments`).catch(() => ({})),
          apiFetch(`/api/customers/${uid}/balance`).catch(() => null),
          fetchPrestations({ customerId: c.id }).catch(() => []),
          fetchProjects({ customerId: c.id }).catch(() => []),
          fetchAllTags().catch(() => []),
          fetchCustomerTags(uid).catch(() => []),
          apiFetch(`/api/analytics/rfm/customer/${uid}`).catch(() => null),
        ]);
        const ordArr = Array.isArray(o?.orders) ? o.orders : (Array.isArray(o) ? o : []);
        setOrders(ordArr);
        setInstallments(Array.isArray(p?.installments) ? p.installments : (Array.isArray(p) ? p : []));
        setBalance(b && typeof b === 'object' ? b : null);
        setPrestations(Array.isArray(pr) ? pr : []);
        setProjects(Array.isArray(pj) ? pj : []);
        setAvailableTags(Array.isArray(allTags) ? allTags : []);
        setCustomerTags(Array.isArray(cTags) ? cTags : []);
        setRfm(rfmData && typeof rfmData === 'object' && rfmData.segment ? rfmData : null);
      }
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (uid) load(); }, [uid]); // eslint-disable-line

  // Chargement lazy des communications (seulement quand l'onglet Historique est ouvert)
  useEffect(() => {
    if (activeTab !== 'historique' || commLoaded || !customer?.id) return;
    setCommLoading(true);
    apiFetch(`/api/relances/communications?customer_id=${customer.id}&limit=50`)
      .then(r => {
        const arr = Array.isArray(r?.items) ? r.items : (Array.isArray(r) ? r : []);
        setCommunications(arr);
      })
      .catch(() => {})
      .finally(() => { setCommLoading(false); setCommLoaded(true); });
  }, [activeTab, commLoaded, customer?.id]); // eslint-disable-line

  // Toggle opt-out rapide
  async function toggleOptOut(field) {
    if (!customer) return;
    const next = !customer[field];
    setCustomer(c => ({ ...c, [field]: next }));
    try {
      await updateCustomerAPI(uid, { [field]: next });
    } catch {
      setCustomer(c => ({ ...c, [field]: !next })); // rollback
    }
  }

  function openEdit() {
    setNewTagInput('');
    setEditForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      type: customer.type || 'particulier',
      is_active: customer.is_active !== false,
      loyalty_level: customer.loyalty_level || 'bronze',
      credit_limit: customer.credit_limit ?? 0,
      credit_status: customer.credit_status || 'active',
      internal_note: customer.internal_note || '',
      email_opt_out: customer.email_opt_out || false,
      whatsapp_opt_out: customer.whatsapp_opt_out || false,
      tagIds: customerTags.map(t => t.id),
    });
    setShowEditModal(true);
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const [updated, updatedTags] = await Promise.all([
        updateCustomerAPI(uid, editForm),
        updateCustomerTags(uid, editForm.tagIds || []),
      ]);
      setCustomer(c => ({ ...c, ...updated }));
      setCustomerTags(Array.isArray(updatedTags) ? updatedTags : []);
      setShowEditModal(false);
    } catch (err) {
      alert(err?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateTag(e) {
    e.preventDefault();
    const name = newTagInput.trim();
    if (!name) return;
    setNewTagLoading(true);
    try {
      const tag = await apiFetch('/api/customers/tags', { method: 'POST', json: { name } });
      setAvailableTags(prev => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
      setEditForm(f => ({ ...f, tagIds: [...(f.tagIds || []), tag.id] }));
      setNewTagInput('');
    } catch (err) {
      alert(err?.message || 'Erreur création tag');
    } finally {
      setNewTagLoading(false);
    }
  }

  function toggleTagInForm(tagId) {
    setEditForm(f => {
      const ids = f.tagIds || [];
      return {
        ...f,
        tagIds: ids.includes(tagId) ? ids.filter(id => id !== tagId) : [...ids, tagId],
      };
    });
  }

  /* ── computed ── */
  const kpis = useMemo(() => {
    const total = orders.reduce((s, o) => s + toNum(o.total_ttc), 0);
    const paid  = orders.reduce((s, o) => s + (toNum(o.paid_amount) || (o.payment_mode === 'cash' ? toNum(o.total_ttc) : 0)), 0);
    const due   = toNum(balance?.total_due, 0);
    return { total, paid, due, count: orders.length };
  }, [orders, balance]);

  /* ══ États de chargement ══ */
  if (loading) return (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>
  );
  if (error) return (
    <div className="m-6 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-700">{error}</div>
  );
  if (!customer) return (
    <div className="m-6 rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Client introuvable.</div>
  );

  const loyalty = LOYALTY_CONFIG[customer.loyalty_level] || LOYALTY_CONFIG.bronze;
  const creditSt = CREDIT_STATUS_CONFIG[customer.credit_status] || CREDIT_STATUS_CONFIG.active;
  const creditPct = customer.credit_limit > 0
    ? Math.min(100, Math.round((toNum(customer.credit_used) / toNum(customer.credit_limit)) * 100))
    : 0;

  const TABS = [
    { key: 'apercu',      label: 'Aperçu',       icon: CheckCircle2 },
    { key: 'commandes',   label: `Commandes (${orders.length})`, icon: ShoppingCart },
    { key: 'prestations', label: `Prestations (${prestations.length})`, icon: Briefcase },
    { key: 'projets',     label: `Projets (${projects.length})`, icon: FolderKanban },
    { key: 'historique',  label: 'Historique',   icon: History },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Header ── */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-6">

          {/* Breadcrumb + actions */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button onClick={() => navigate('/customers')} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
              <ArrowLeft className="h-4 w-4" /> Clients
            </button>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowStatement(s => !s)}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <FileText className="h-4 w-4" /> Relevé de compte
                {showStatement ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              <button
                onClick={openEdit}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Pencil className="h-4 w-4" /> Modifier
              </button>
            </div>
          </div>

          {/* Panneau relevé de compte */}
          {showStatement && (
            <div className="mt-3 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Du</label>
                <input
                  type="date"
                  value={statementFrom}
                  onChange={e => setStatementFrom(e.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Au</label>
                <input
                  type="date"
                  value={statementTo}
                  onChange={e => setStatementTo(e.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>
              <button
                onClick={downloadStatement}
                disabled={statementLoading || !statementFrom || !statementTo}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {statementLoading
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Génération…</>
                  : <><Download className="h-4 w-4" /> Télécharger PDF</>
                }
              </button>
            </div>
          )}

          {/* Nom + badges */}
          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-black text-slate-900">{customer.name}</h1>
                <Pill tone={customer.is_active ? 'green' : 'red'}>
                  {customer.is_active ? 'Actif' : 'Inactif'}
                </Pill>
                <Pill tone={customer.type === 'entreprise' ? 'blue' : customer.type === 'revendeur' ? 'purple' : 'neutral'}>
                  {customer.type === 'entreprise' ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
                  {customer.type || 'particulier'}
                </Pill>
                {rfm?.segment ? (() => {
                  const RFM_HDR = {
                    champion:   { label: 'Champion',    emoji: '🥇', tone: 'green'   },
                    loyal:      { label: 'Fidèle',      emoji: '🥈', tone: 'blue'    },
                    new:        { label: 'Nouveau',     emoji: '🌱', tone: 'purple'  },
                    at_risk:    { label: 'À risque',    emoji: '⚠️', tone: 'yellow'  },
                    occasional: { label: 'Occasionnel', emoji: '🔄', tone: 'neutral' },
                    lost:       { label: 'Perdu',       emoji: '💤', tone: 'red'     },
                  }
                  const s = RFM_HDR[rfm.segment] || RFM_HDR.occasional
                  return (
                    <Pill tone={s.tone}>
                      {s.emoji} {s.label}
                    </Pill>
                  )
                })() : (
                  <Pill tone={loyalty.tone}>
                    {loyalty.icon} {loyalty.label}
                  </Pill>
                )}
                <span className={cx('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1', creditSt.cls)}>
                  <CreditCard className="h-3 w-3" /> {creditSt.label}
                </span>
                {customerTags.map(t => (
                  <Pill key={t.id} tone={tagTone(t.name)}>
                    <Tag className="h-3 w-3" /> {t.name}
                  </Pill>
                ))}
              </div>

              {/* Coordonnées */}
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                {customer.phone && (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-slate-400" /> {customer.phone}
                  </span>
                )}
                {customer.email && (
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 text-slate-400" /> {customer.email}
                  </span>
                )}
                {customer.address && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" /> {customer.address}
                  </span>
                )}
              </div>
            </div>

            {/* Opt-out toggles */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => toggleOptOut('email_opt_out')}
                  title={customer.email_opt_out ? 'Réactiver emails' : 'Désactiver emails'}
                  className={cx(
                    'inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium transition',
                    customer.email_opt_out
                      ? 'border-rose-200 bg-rose-50 text-rose-600'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  )}
                >
                  {customer.email_opt_out
                    ? <><BellOff className="h-4 w-4" /> Email désactivé</>
                    : <><Mail className="h-4 w-4" /> Email actif</>
                  }
                </button>
                <button
                  onClick={() => toggleOptOut('whatsapp_opt_out')}
                  title={customer.whatsapp_opt_out ? 'Réactiver WhatsApp' : 'Désactiver WhatsApp'}
                  className={cx(
                    'inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium transition',
                    customer.whatsapp_opt_out
                      ? 'border-rose-200 bg-rose-50 text-rose-600'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  )}
                >
                  {customer.whatsapp_opt_out
                    ? <><MessageSquareOff className="h-4 w-4" /> WA désactivé</>
                    : <><MessageSquareOff className="h-4 w-4" /> WA actif</>
                  }
                </button>
              </div>
              {/* Actions rapides */}
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to="/orders"
                  className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                >
                  <Plus className="h-3.5 w-3.5" /> Commande
                </Link>
                <Link
                  to="/relances"
                  className="inline-flex items-center gap-1.5 rounded-2xl border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100"
                >
                  <Bell className="h-3.5 w-3.5" /> Relance
                </Link>
                <Link
                  to="/agenda"
                  className="inline-flex items-center gap-1.5 rounded-2xl border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                >
                  <CalendarDays className="h-3.5 w-3.5" /> RDV
                </Link>
                <button
                  onClick={() => setActiveTab('historique')}
                  className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  <History className="h-3.5 w-3.5" /> Historique
                </button>
              </div>
            </div>
          </div>

          {/* KPI row */}
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard icon={DollarSign}    label="Total commandé"  value={fmtMoney(kpis.total)} sub={`${kpis.count} commandes`} />
            <KpiCard icon={CheckCircle2}  label="Encaissé"        value={fmtMoney(kpis.paid)} tone="green" />
            <KpiCard icon={AlertTriangle} label="Reste dû"        value={fmtMoney(kpis.due)}  tone={kpis.due > 0 ? 'red' : 'neutral'} />
            {rfm?.segment ? (() => {
              const RFM_SEG = {
                champion:   { label: 'Champion',    emoji: '🥇' },
                loyal:      { label: 'Fidèle',      emoji: '🥈' },
                new:        { label: 'Nouveau',     emoji: '🌱' },
                at_risk:    { label: 'À risque',    emoji: '⚠️' },
                occasional: { label: 'Occasionnel', emoji: '🔄' },
                lost:       { label: 'Perdu',       emoji: '💤' },
              }
              const seg = RFM_SEG[rfm.segment] || RFM_SEG.occasional
              return <KpiCard icon={Star} label="Segment RFM" value={`${seg.emoji} ${seg.label}`} sub={`R${rfm.r_score}·F${rfm.f_score}·M${rfm.m_score}`} tone="neutral" />
            })() : (
              <KpiCard icon={Star} label="Fidélité" value={loyalty.label} sub={`Crédit: ${fmtMoney(customer.credit_limit ?? 0)}`} tone="neutral" />
            )}
          </div>

          {/* Stats secondaires */}
          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            {[
              {
                label: 'Panier moyen',
                value: rfm?.avg_order_value > 0 ? fmtMoney(rfm.avg_order_value) : (orders.length > 0 ? fmtMoney(kpis.total / orders.length) : '—'),
              },
              {
                label: 'Nb commandes',
                value: rfm?.frequency != null ? rfm.frequency : orders.length,
              },
              {
                label: 'Membre depuis',
                value: customer.created_at ? fmtDate(customer.created_at) : '—',
              },
              {
                label: 'Dernier achat',
                value: customer.last_purchase_at
                  ? fmtDate(customer.last_purchase_at)
                  : (rfm?.recency_days != null ? `il y a ${rfm.recency_days}j` : '—'),
              },
            ].map(s => (
              <div key={s.label} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5">
                <div className="text-[11px] font-medium text-slate-500">{s.label}</div>
                <div className="mt-0.5 text-sm font-bold text-slate-800">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Note interne */}
          {customer.internal_note && (
            <div className="mt-4 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <FileText className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
              <span>{customer.internal_note}</span>
            </div>
          )}

          {/* Crédit barre */}
          {toNum(customer.credit_limit) > 0 && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="font-medium text-slate-700">Crédit utilisé</span>
                <span>{fmtMoney(customer.credit_used)} / {fmtMoney(customer.credit_limit)}</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cx('h-full rounded-full transition-all', creditPct > 85 ? 'bg-rose-500' : creditPct > 60 ? 'bg-amber-500' : 'bg-emerald-500')}
                  style={{ width: `${creditPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Onglets */}
          <div className="mt-5 flex gap-1 border-b border-slate-200">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={cx(
                  'inline-flex items-center gap-2 rounded-t-xl px-4 py-2.5 text-sm font-semibold transition-colors -mb-px',
                  activeTab === key
                    ? 'border border-b-white border-slate-200 bg-white text-slate-900'
                    : 'text-slate-500 hover:text-slate-700'
                )}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Contenu onglets ── */}
      <div className="mx-auto max-w-6xl px-6 py-6 space-y-5">

        {/* Aperçu */}
        {activeTab === 'apercu' && (
          <>
            <RemindersBanner installments={installments} />
            <BalanceBlock balance={balance} />

            {/* Bloc RFM — fidélité calculée depuis l'historique */}
            {rfm?.segment && (() => {
              const RFM_SEG = {
                champion:   { label: 'Champion',    emoji: '🥇', cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', barCls: 'bg-emerald-400' },
                loyal:      { label: 'Fidèle',      emoji: '🥈', cls: 'bg-blue-50 border-blue-200 text-blue-800',          barCls: 'bg-blue-400'    },
                new:        { label: 'Nouveau',     emoji: '🌱', cls: 'bg-violet-50 border-violet-200 text-violet-800',    barCls: 'bg-violet-400'  },
                at_risk:    { label: 'À risque',    emoji: '⚠️', cls: 'bg-amber-50 border-amber-200 text-amber-800',       barCls: 'bg-amber-400'   },
                occasional: { label: 'Occasionnel', emoji: '🔄', cls: 'bg-slate-50 border-slate-200 text-slate-700',       barCls: 'bg-slate-400'   },
                lost:       { label: 'Perdu',       emoji: '💤', cls: 'bg-rose-50 border-rose-200 text-rose-800',          barCls: 'bg-rose-400'    },
              }
              const seg = RFM_SEG[rfm.segment] || RFM_SEG.occasional
              const dims = [
                {
                  key: 'R', label: 'Récence', score: rfm.r_score,
                  detail: rfm.recency_days != null
                    ? `Dernier achat il y a ${rfm.recency_days} j`
                    : 'Aucun achat récent',
                  tip: 'Score élevé = achat récent',
                },
                {
                  key: 'F', label: 'Fréquence', score: rfm.f_score,
                  detail: `${rfm.frequency ?? 0} commande${(rfm.frequency ?? 0) > 1 ? 's' : ''} au total`,
                  tip: 'Score élevé = client fidèle',
                },
                {
                  key: 'M', label: 'Montant', score: rfm.m_score,
                  detail: fmtMoney(rfm.monetary ?? 0) + ' dépensé',
                  tip: rfm.avg_order_value ? `Panier moyen: ${fmtMoney(rfm.avg_order_value)}` : '',
                },
              ]
              return (
                <div className={cx('rounded-2xl border p-5', seg.cls)}>
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{seg.emoji}</span>
                        <span className="text-lg font-black">{seg.label}</span>
                        <span className="text-sm font-semibold opacity-60">R{rfm.r_score}·F{rfm.f_score}·M{rfm.m_score}</span>
                      </div>
                      <div className="text-xs opacity-60 mt-0.5">Score calculé automatiquement depuis l'historique des commandes</div>
                    </div>
                    <button
                      onClick={() => setActiveTab('commandes')}
                      className="text-xs font-semibold underline underline-offset-2 opacity-70 hover:opacity-100"
                    >
                      Voir {rfm.frequency ?? 0} commandes →
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {dims.map(d => (
                      <div key={d.key} className="bg-white bg-opacity-60 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold uppercase tracking-wide opacity-70">{d.key} — {d.label}</span>
                          <span className="text-sm font-black">{d.score}/4</span>
                        </div>
                        <div className="flex gap-0.5 mb-2">
                          {[1,2,3,4].map(i => (
                            <div key={i} className={cx('h-1.5 flex-1 rounded-full', i <= d.score ? seg.barCls : 'bg-black bg-opacity-10')} />
                          ))}
                        </div>
                        <div className="text-xs font-semibold opacity-80">{d.detail}</div>
                        {d.tip && <div className="text-[10px] opacity-50 mt-0.5">{d.tip}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
            {installments.length > 0 && (
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4">
                <div className="text-sm text-slate-600">Gérez les échéances et encaissements depuis la page centralisée.</div>
                <Link
                  to={`/installments?customer_uid=${customer.uid}`}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Ouvrir l'échéancier
                </Link>
              </div>
            )}
            {!balance && installments.length === 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                Aucune donnée financière disponible.
              </div>
            )}
          </>
        )}

        {/* Commandes */}
        {activeTab === 'commandes' && (
          <div className="space-y-3">
          {rfm?.segment && (() => {
            const RFM_SEG = {
              champion:   { label: 'Champion',    emoji: '🥇', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
              loyal:      { label: 'Fidèle',      emoji: '🥈', cls: 'bg-blue-50 border-blue-200 text-blue-700'          },
              new:        { label: 'Nouveau',     emoji: '🌱', cls: 'bg-violet-50 border-violet-200 text-violet-700'    },
              at_risk:    { label: 'À risque',    emoji: '⚠️', cls: 'bg-amber-50 border-amber-200 text-amber-700'       },
              occasional: { label: 'Occasionnel', emoji: '🔄', cls: 'bg-slate-50 border-slate-200 text-slate-600'       },
              lost:       { label: 'Perdu',       emoji: '💤', cls: 'bg-rose-50 border-rose-200 text-rose-700'          },
            }
            const seg = RFM_SEG[rfm.segment] || RFM_SEG.occasional
            return (
              <div className={cx('flex flex-wrap items-center gap-4 rounded-2xl border px-4 py-3 text-sm', seg.cls)}>
                <span className="font-black">{seg.emoji} {seg.label}</span>
                <span className="opacity-70">·</span>
                <span><strong>{rfm.frequency ?? 0}</strong> commandes</span>
                <span className="opacity-70">·</span>
                <span>Dernier achat il y a <strong>{rfm.recency_days ?? '?'} j</strong></span>
                <span className="opacity-70">·</span>
                <span><strong>{fmtMoney(rfm.monetary ?? 0)}</strong> dépensé au total</span>
                {rfm.avg_order_value > 0 && <>
                  <span className="opacity-70">·</span>
                  <span>Panier moyen <strong>{fmtMoney(rfm.avg_order_value)}</strong></span>
                </>}
              </div>
            )
          })()}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            {orders.length === 0 ? (
              <div className="p-10 text-center text-slate-500">Aucune commande enregistrée.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {orders.map((o, idx) => {
                  const key = String(o.external_id ?? o.id ?? idx);
                  return (
                    <div key={key} className="flex items-center justify-between px-5 py-4 hover:bg-slate-50">
                      <div>
                        <div className="font-semibold text-slate-900">{o.external_id || `CMD-${o.id || idx + 1}`}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{fmtDate(o.created_at || o.date)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-900">{fmtMoney(o.total_ttc)}</div>
                        <div className="text-xs text-slate-500">
                          {o.items_count ? `${o.items_count} art.` : ''}
                          {o.payment_mode ? ` • ${o.payment_mode}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        )}

        {/* Prestations */}
        {activeTab === 'prestations' && (
          <div className="space-y-3">
            {prestations.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                Aucune prestation liée à ce client.
              </div>
            ) : prestations.map((p) => {
              const st = PRESTATION_STATUS[p.status] || PRESTATION_STATUS.DRAFT;
              return (
                <div key={p.id} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 truncate">{p.title}</span>
                      <Pill tone={st.tone}>{st.label}</Pill>
                      {p.invoice_number && <Pill tone="blue">{p.invoice_number}</Pill>}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {p.code} {p.service_date ? `• ${fmtDate(p.service_date)}` : ''} {p.payment_mode ? `• ${p.payment_mode === 'ONE_SHOT' ? 'Paiement unique' : 'Échelonné'}` : ''}
                    </div>
                  </div>
                  <div className="ml-4 text-right shrink-0">
                    <div className="font-bold text-slate-900">{fmtMoney(p.amount_ttc || p.amount)}</div>
                    {toNum(p.due_total) > 0 && (
                      <div className="text-xs font-medium text-rose-600">Reste: {fmtMoney(p.due_total)}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Historique */}
        {activeTab === 'historique' && (
          <div className="space-y-4">
            {/* Légende */}
            <div className="flex flex-wrap gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-sky-300" /> Commandes</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-violet-300" /> Prestations</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-300" /> Emails</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-300" /> WhatsApp</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-300" /> Échéances en retard</span>
            </div>
            {commLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                <span className="ml-3 text-sm text-slate-500">Chargement des communications…</span>
              </div>
            ) : (
              <UnifiedTimeline
                orders={orders}
                prestations={prestations}
                communications={communications}
                installments={installments}
              />
            )}
          </div>
        )}

        {/* Projets */}
        {activeTab === 'projets' && (
          <div className="space-y-3">
            {projects.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                Aucun projet lié à ce client.
              </div>
            ) : projects.map((proj) => {
              const st = PROJECT_STATUS[proj.status] || PROJECT_STATUS.DRAFT;
              return (
                <div key={proj.id} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 truncate">{proj.title}</span>
                        <Pill tone={st.tone}>{st.label}</Pill>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {proj.code} {proj.start_date ? `• Début ${fmtDate(proj.start_date)}` : ''}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-slate-900">{fmtMoney(proj.total_amount_calc)}</div>
                      <div className="text-xs text-slate-500">{proj.prestations_count || 0} prestation(s)</div>
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-slate-900" style={{ width: `${st.pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Modal édition ── */}
      {showEditModal && (
        <ModalShell title="Modifier le client" onClose={() => setShowEditModal(false)}>
          <form onSubmit={handleSaveEdit} className="space-y-5">

            {/* Infos de base */}
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Informations</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold text-slate-900">Nom *</label>
                  <input required value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Téléphone</label>
                  <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Email</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Type</label>
                  <select value={editForm.type} onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100">
                    <option value="particulier">Particulier</option>
                    <option value="entreprise">Entreprise</option>
                    <option value="revendeur">Revendeur</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900">Adresse</label>
                  <input value={editForm.address} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-900">
                  <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 accent-slate-900" />
                  Client actif
                </label>
              </div>
            </div>

            {/* Fidélité & Crédit */}
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Fidélité & Crédit</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="text-sm font-semibold text-slate-900">Niveau fidélité</label>
                  <select value={editForm.loyalty_level} onChange={e => setEditForm(f => ({ ...f, loyalty_level: e.target.value }))}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100">
                    <option value="bronze">🥉 Bronze</option>
                    <option value="silver">🥈 Silver</option>
                    <option value="gold">🥇 Gold</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Limite crédit (FCFA)</label>
                  <input type="number" min="0" value={editForm.credit_limit} onChange={e => setEditForm(f => ({ ...f, credit_limit: Number(e.target.value) }))}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-900">Statut crédit</label>
                  <select value={editForm.credit_status} onChange={e => setEditForm(f => ({ ...f, credit_status: e.target.value }))}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100">
                    <option value="active">Actif</option>
                    <option value="suspended">Suspendu</option>
                    <option value="blocked">Bloqué</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Communication */}
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Communication</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50">
                  <input type="checkbox" checked={editForm.email_opt_out} onChange={e => setEditForm(f => ({ ...f, email_opt_out: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 accent-slate-900" />
                  Opt-out email
                </label>
                <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50">
                  <input type="checkbox" checked={editForm.whatsapp_opt_out} onChange={e => setEditForm(f => ({ ...f, whatsapp_opt_out: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 accent-slate-900" />
                  Opt-out WhatsApp
                </label>
              </div>
              <div className="mt-3">
                <label className="text-sm font-semibold text-slate-900">Note interne</label>
                <textarea value={editForm.internal_note} onChange={e => setEditForm(f => ({ ...f, internal_note: e.target.value }))}
                  rows={3} placeholder="Note visible uniquement en interne…"
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100" />
              </div>
            </div>

            {/* Tags */}
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Tags</div>
              {availableTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {availableTags.map(t => {
                    const active = (editForm.tagIds || []).includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleTagInForm(t.id)}
                        className={cx(
                          'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition',
                          active
                            ? 'bg-slate-900 text-white ring-slate-900'
                            : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300'
                        )}
                      >
                        <Tag className="h-3 w-3" /> {t.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <form onSubmit={handleCreateTag} className="flex items-center gap-2">
                <input
                  value={newTagInput}
                  onChange={e => setNewTagInput(e.target.value)}
                  placeholder="Nouveau tag…"
                  maxLength={50}
                  className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
                <button
                  type="submit"
                  disabled={!newTagInput.trim() || newTagLoading}
                  className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {newTagLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Créer
                </button>
              </form>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setShowEditModal(false)}
                className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50">
                Annuler
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Enregistrement…</> : 'Sauvegarder'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}
